// ============================
// File: src/pages/TasksPage.jsx
// Purpose: Show list of demands returned to user for fixing gaps
// ============================

import React, { useCallback, useState, useEffect } from "react";
import DemandEditPage from "./DemandEditPage";
import { listDemands, getClarifyTasks } from "../api/demandsClient";

export default function TasksPage() {
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [error, setError] = useState(null);
  const [selectedDemandId, setSelectedDemandId] = useState(null);

  // Load demands that were returned to their submitter, with their gap tasks.
  // This body used to be duplicated verbatim in handleBackToList.
  const loadTasks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await listDemands({ limit: 200 });
      const returnedDemands = (result.demands || []).filter(
        (d) => d.current_stage === 'TRIAGE_RETURNED' || d.stage_status === 'PENDING_FIXES'
      );

      const demandsWithTasks = await Promise.all(
        returnedDemands.map(async (demand) => {
          try {
            const tasksResult = await getClarifyTasks(demand.demand_id);
            return { ...demand, gaps: tasksResult.tasks || [] };
          } catch {
            // One demand's tasks failing shouldn't hide the rest of the list.
            return { ...demand, gaps: [], gapsError: true };
          }
        })
      );

      setTasks(demandsWithTasks);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  const handleEditDemand = (demandId) => {
    setSelectedDemandId(demandId);
  };

  const handleBackToList = () => {
    setSelectedDemandId(null);
    loadTasks();
  };

  // If a demand is selected, show the edit page
  if (selectedDemandId) {
    return (
      <div>
        <button
          onClick={handleBackToList}
          style={{
            backgroundColor: 'var(--cares-surface-raised)',
            color: 'var(--cares-text-max)',
            padding: '8px 16px',
            borderRadius: '6px',
            border: 'none',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            marginBottom: '16px',
          }}
        >
          ← Back to Tasks List
        </button>
        <DemandEditPage demandId={selectedDemandId} onSaveComplete={handleBackToList} />
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '16px', color: 'var(--cares-text-label)' }}>
          Loading your tasks...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '16px', color: 'var(--cares-orange)' }}>
          ❌ Error: {error}
        </div>
      </div>
    );
  }

  if (tasks.length === 0) {
    return (
      <div style={{ padding: '40px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '16px' }}>
          My Tasks
        </h1>
        <div style={{
          backgroundColor: 'var(--cares-surface-board)',
          border: '1px solid var(--cares-line-card)',
          borderRadius: '8px',
          padding: '40px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
          <div style={{ fontSize: '18px', fontWeight: '600', color: 'var(--cares-text-3)', marginBottom: '8px' }}>
            No Tasks Pending
          </div>
          <div style={{ fontSize: '14px', color: 'var(--cares-text-label)' }}>
            You don't have any demands that need fixes. Great job!
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '40px' }}>
      <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>
        My Tasks
      </h1>
      <p style={{ fontSize: '14px', color: 'var(--cares-text-label)', marginBottom: '24px' }}>
        Demands returned from triage that need your attention
      </p>

      <div style={{ display: 'grid', gap: '16px' }}>
        {tasks.map((task) => (
          <div
            key={task.demand_id}
            style={{
              backgroundColor: 'var(--cares-surface-card)',
              border: '1px solid var(--cares-line-card)',
              borderRadius: '8px',
              padding: '24px',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px' }}>
              <div style={{ flex: 1 }}>
                <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--cares-text-1)', marginBottom: '8px' }}>
                  {task.title}
                </h2>
                <div style={{ display: 'flex', gap: '16px', fontSize: '14px', color: 'var(--cares-text-label)' }}>
                  <span>📋 {task.demand_id}</span>
                  <span>📊 Score: {task.triage_score || 'N/A'}/100</span>
                  <span>🔴 {task.gaps.length} gaps to fix</span>
                </div>
              </div>
              <button
                onClick={() => handleEditDemand(task.demand_id)}
                style={{
                  backgroundColor: 'var(--cares-blue)',
                  color: 'var(--cares-text-max)',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                }}
              >
                Fix Gaps →
              </button>
            </div>

            {/* Gap List Preview */}
            {task.gaps.length > 0 && (
              <div style={{
                backgroundColor: 'color-mix(in srgb, var(--cares-orange) 14%, var(--cares-surface-card))',
                border: '1px solid color-mix(in srgb, var(--cares-orange) 30%, var(--cares-surface-card))',
                borderRadius: '6px',
                padding: '12px',
              }}>
                <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--cares-orange)', marginBottom: '8px' }}>
                  Gaps to Address:
                </div>
                <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: 'var(--cares-orange)' }}>
                  {task.gaps.slice(0, 3).map((gap, idx) => (
                    <li key={idx} style={{ marginBottom: '4px' }}>
                      <strong>{gap.field_name}:</strong> {gap.issue_description}
                    </li>
                  ))}
                  {task.gaps.length > 3 && (
                    <li style={{ color: 'var(--cares-text-label)', fontStyle: 'italic' }}>
                      ... and {task.gaps.length - 3} more
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

